import React, { useEffect, useId, useLayoutEffect, useMemo, useRef } from 'react';
import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion';

const TRIANGLE_SIZE = 100;
const ROWS = 20;
const COLS = 30;
const SVG_WIDTH = COLS * TRIANGLE_SIZE * 0.866;
const SVG_HEIGHT = ROWS * TRIANGLE_SIZE * 0.75 + TRIANGLE_SIZE * 0.25;
const HIGHLIGHT_RADIUS = 200;
const INITIAL_POSITION = { x: 0, y: 0 };

// Ambient mode: the glow starts drifting IDLE_DELAY ms after the last pointer
// input and follows a slow Lissajous path through the visible part of the grid.
const IDLE_DELAY = 3000;
const IDLE_PERIOD_X = 60000;
const IDLE_PERIOD_Y = 44000;
const IDLE_AMPLITUDE = 0.3;
// Share of the remaining distance the glow covers per frame, and the maximum
// distance in SVG units it may travel per frame while drifting.
const POINTER_FOLLOW = 0.25;
const IDLE_FOLLOW = 0.02;
const IDLE_MAX_STEP = 2.5;
// Caps the time step so the drift does not jump after a stalled frame.
const MAX_FRAME_TIME = 100;

const buildGrid = () => {
    const triangles = [];
    const lines = [];

    const addLine = (key, from, to) => {
        lines.push({
            key,
            x1: from.x,
            y1: from.y,
            x2: to.x,
            y2: to.y,
            cx: (from.x + to.x) / 2,
            cy: (from.y + to.y) / 2,
        });
    };

    for (let row = 0; row < ROWS; row++) {
        for (let col = 0; col < COLS; col++) {
            // 0.866 = cos(30deg), 0.433 = half of that; odd rows are shifted
            // by half a triangle so neighbouring rows tessellate.
            const x = col * TRIANGLE_SIZE * 0.866;
            const y = row * TRIANGLE_SIZE * 0.75;
            const offset = row % 2 === 0 ? 0 : TRIANGLE_SIZE * 0.433;

            const p1 = { x: x + offset, y: y + TRIANGLE_SIZE * 0.75 };
            const p2 = { x: x + offset + TRIANGLE_SIZE * 0.433, y: y };
            const p3 = { x: x + offset + TRIANGLE_SIZE * 0.866, y: y + TRIANGLE_SIZE * 0.75 };
            const p4 = { x: x + offset + TRIANGLE_SIZE * 0.433, y: y + TRIANGLE_SIZE * 1.5 };

            triangles.push({ key: `up-${row}-${col}`, d: `M ${p1.x} ${p1.y} L ${p2.x} ${p2.y} L ${p3.x} ${p3.y} Z` });
            triangles.push({ key: `down-${row}-${col}`, d: `M ${p1.x} ${p1.y} L ${p3.x} ${p3.y} L ${p4.x} ${p4.y} Z` });

            addLine(`line1-${row}-${col}`, p1, p3);
            addLine(`line2-${row}-${col}`, p1, p2);
            addLine(`line3-${row}-${col}`, p3, p2);
            if (row < ROWS - 1) {
                addLine(`line4-${row}-${col}`, p1, p4);
            }
        }
    }

    return { triangles, lines };
};

const GRID = buildGrid();

// Blends a line from the base tone to the primary color based on how close
// its midpoint is to the glow, and makes it thicker and more opaque.
const getLineStyle = (line, position, baseTone, primary) => {
    const distance = Math.hypot(position.x - line.cx, position.y - line.cy);
    const brightness = 1 - Math.min(distance / HIGHLIGHT_RADIUS, 1);

    const r = baseTone.r + (primary.r - baseTone.r) * brightness;
    const g = baseTone.g + (primary.g - baseTone.g) * brightness;
    const b = baseTone.b + (primary.b - baseTone.b) * brightness;
    const opacity = 0.4 + brightness * 0.6;

    return {
        color: `rgba(${r}, ${g}, ${b}, ${opacity})`,
        width: 1 + brightness * 2,
        brightness,
    };
};

/**
 * Full-size triangular grid whose lines light up around a glow that follows
 * the mouse pointer.
 *
 * The grid is rendered once; pointer updates change the affected lines
 * directly in the DOM inside requestAnimationFrame callbacks.
 *
 * @param {object} props
 * @param {{r: number, g: number, b: number}} props.primaryColor - Highlight color of the lines and the glow.
 * @param {string} props.backgroundColor - 'transparent', 'auto' (dark tone derived from primaryColor) or any CSS color.
 * @param {number} props.opacity - Opacity of the grid.
 * @param {boolean} props.triangleFill - Whether the triangles get a fill color.
 * @param {boolean} props.ambient - Also follow touch input and let the glow drift slowly while
 *   there is no pointer input. The drift is disabled when the user prefers reduced motion and
 *   pauses while the tab is hidden.
 * @param {Function} [props.onGlowMove] - Called whenever the glow moves, with its position in
 *   viewport coordinates ({x, y}) and whether it follows the pointer (true) or drifts (false).
 */
const TriangularGridBackground = ({
    primaryColor = { r: 255, g: 140, b: 0 },
    backgroundColor = 'transparent',
    opacity = 1,
    triangleFill = true,
    ambient = false,
    onGlowMove,
}) => {
    const containerRef = useRef(null);
    const svgRef = useRef(null);
    const glowRef = useRef(null);
    const positionRef = useRef(null);
    const onGlowMoveRef = useRef(onGlowMove);
    const glowId = `grid-glow-${useId().replace(/:/g, '')}`;
    const reducedMotion = usePrefersReducedMotion();

    const { r, g, b } = primaryColor;
    const primary = useMemo(() => ({ r, g, b }), [r, g, b]);
    const baseTone = useMemo(
        () => ({ r: Math.max(20, r * 0.08), g: Math.max(30, g * 0.12), b: Math.max(50, b * 0.2) }),
        [r, g, b]
    );

    const background = backgroundColor === 'auto'
        ? `rgb(${Math.max(5, r * 0.02)}, ${Math.max(8, g * 0.03)}, ${Math.max(15, b * 0.05)})`
        : backgroundColor;
    const triangleFillColor = !triangleFill
        ? 'transparent'
        : backgroundColor === 'transparent' ? 'rgba(0, 0, 0, 0.03)' : background;

    useEffect(() => {
        onGlowMoveRef.current = onGlowMove;
    }, [onGlowMove]);

    const lineElements = useMemo(() => GRID.lines.map((line) => {
        const style = getLineStyle(line, INITIAL_POSITION, baseTone, primary);
        return (
            <line
                key={line.key}
                x1={line.x1} y1={line.y1}
                x2={line.x2} y2={line.y2}
                stroke={style.color}
                strokeWidth={style.width}
                strokeLinecap="round"
            />
        );
    }), [baseTone, primary]);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const svg = svgRef.current;
        const glow = glowRef.current;
        if (!container || !svg || !glow) return undefined;

        const lineNodes = svg.querySelectorAll('line');
        const idleEnabled = ambient && !reducedMotion;
        let idleTime = 0;
        let lastFrameTime = null;
        let litLines = new Set();
        let frameId = null;
        let target = null;
        let lastInput = -Infinity;
        let applied = null;
        let followsPointer = false;

        // Converts viewport coordinates to SVG user units, taking the cropping
        // of preserveAspectRatio="slice" into account.
        const toSvgPoint = (clientX, clientY) => {
            const matrix = typeof svg.getScreenCTM === 'function' ? svg.getScreenCTM() : null;
            if (!matrix) return null;
            const inverse = matrix.inverse();
            return {
                x: inverse.a * clientX + inverse.c * clientY + inverse.e,
                y: inverse.b * clientX + inverse.d * clientY + inverse.f,
            };
        };

        const getVisibleBox = () => {
            const rect = container.getBoundingClientRect();
            const top = Math.max(rect.top, 0);
            const bottom = Math.min(rect.bottom, window.innerHeight);
            const start = toSvgPoint(rect.left, top);
            const end = toSvgPoint(rect.right, bottom);
            if (!start || !end || end.y <= start.y) {
                return { x: 0, y: 0, width: SVG_WIDTH, height: SVG_HEIGHT };
            }
            return { x: start.x, y: start.y, width: end.x - start.x, height: end.y - start.y };
        };

        const getIdlePosition = () => {
            const box = getVisibleBox();
            return {
                x: box.x + box.width * (0.5 + IDLE_AMPLITUDE * Math.sin((2 * Math.PI * idleTime) / IDLE_PERIOD_X)),
                y: box.y + box.height * (0.5 + IDLE_AMPLITUDE * Math.sin((2 * Math.PI * idleTime) / IDLE_PERIOD_Y + 1)),
            };
        };

        // Only lines near the new or the previous glow position change, so
        // the rest of the grid is skipped unless a full refresh is needed.
        const apply = (position, fullRefresh = false) => {
            glow.setAttribute('cx', position.x);
            glow.setAttribute('cy', position.y);

            const nextLit = new Set();
            GRID.lines.forEach((line, index) => {
                const isNear = Math.abs(line.cx - position.x) < HIGHLIGHT_RADIUS
                    && Math.abs(line.cy - position.y) < HIGHLIGHT_RADIUS;
                if (!fullRefresh && !isNear && !litLines.has(index)) return;

                const style = getLineStyle(line, position, baseTone, primary);
                lineNodes[index].setAttribute('stroke', style.color);
                lineNodes[index].setAttribute('stroke-width', style.width);
                if (style.brightness > 0) nextLit.add(index);
            });

            litLines = nextLit;
            applied = position;
            positionRef.current = position;

            const matrix = onGlowMoveRef.current && typeof svg.getScreenCTM === 'function' ? svg.getScreenCTM() : null;
            if (matrix) {
                onGlowMoveRef.current(
                    {
                        x: matrix.a * position.x + matrix.c * position.y + matrix.e,
                        y: matrix.b * position.x + matrix.d * position.y + matrix.f,
                    },
                    followsPointer
                );
            }
        };

        const schedule = () => {
            if (frameId === null && !document.hidden) {
                frameId = requestAnimationFrame(tick);
            }
        };

        function tick(time) {
            frameId = null;
            // The idle clock only advances while frames are rendered, so the
            // drift continues where it stopped when a hidden tab is shown again.
            idleTime += lastFrameTime === null ? 0 : Math.min(time - lastFrameTime, MAX_FRAME_TIME);
            lastFrameTime = time;

            followsPointer = target !== null && (!idleEnabled || time - lastInput < IDLE_DELAY);
            const goal = followsPointer ? target : idleEnabled ? getIdlePosition() : null;
            if (!goal) return;

            const factor = !ambient ? 1 : followsPointer ? POINTER_FOLLOW : IDLE_FOLLOW;
            let stepX = (goal.x - applied.x) * factor;
            let stepY = (goal.y - applied.y) * factor;
            const stepLength = Math.hypot(stepX, stepY);
            if (!followsPointer && stepLength > IDLE_MAX_STEP) {
                stepX *= IDLE_MAX_STEP / stepLength;
                stepY *= IDLE_MAX_STEP / stepLength;
            }
            if (stepLength > 0.1) {
                apply({ x: applied.x + stepX, y: applied.y + stepY });
            }
            if (idleEnabled || Math.hypot(goal.x - applied.x, goal.y - applied.y) > 0.5) {
                schedule();
            }
        }

        const handlePointer = (clientX, clientY) => {
            const point = toSvgPoint(clientX, clientY);
            if (!point) return;
            target = point;
            lastInput = performance.now();
            schedule();
        };

        const handleMouseMove = (event) => handlePointer(event.clientX, event.clientY);

        const handleTouch = (event) => {
            const touch = event.touches[0];
            if (touch) handlePointer(touch.clientX, touch.clientY);
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                if (frameId !== null) cancelAnimationFrame(frameId);
                frameId = null;
                lastFrameTime = null;
            } else if (idleEnabled) {
                schedule();
            }
        };

        const startPosition = positionRef.current
            ?? (idleEnabled ? getIdlePosition() : INITIAL_POSITION);
        apply(startPosition, true);

        window.addEventListener('mousemove', handleMouseMove, { passive: true });
        if (ambient) {
            window.addEventListener('touchstart', handleTouch, { passive: true });
            window.addEventListener('touchmove', handleTouch, { passive: true });
        }
        document.addEventListener('visibilitychange', handleVisibilityChange);
        if (idleEnabled) schedule();

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('touchstart', handleTouch);
            window.removeEventListener('touchmove', handleTouch);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (frameId !== null) cancelAnimationFrame(frameId);
        };
    }, [ambient, reducedMotion, baseTone, primary]);

    return (
        <div
            ref={containerRef}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                zIndex: 1
            }}
        >
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(to bottom, transparent, transparent, rgba(0,0,0,0.5))',
                pointerEvents: 'none',
                zIndex: 10
            }} />

            <svg
                ref={svgRef}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: opacity,
                }}
                viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
                preserveAspectRatio="xMidYMid slice"
            >
                {backgroundColor !== 'transparent' && (
                    <rect width="100%" height="100%" fill={background} />
                )}
                {GRID.triangles.map((triangle) => (
                    <path key={triangle.key} d={triangle.d} fill={triangleFillColor} stroke="none" />
                ))}
                {lineElements}

                <defs>
                    <radialGradient id={glowId}>
                        <stop offset="0%" stopColor={`rgba(${r}, ${g}, ${b}, 0.3)`} />
                        <stop offset="50%" stopColor={`rgba(${r}, ${g}, ${b}, 0.1)`} />
                        <stop offset="100%" stopColor="rgba(0, 0, 0, 0)" />
                    </radialGradient>
                </defs>

                <circle
                    ref={glowRef}
                    cx={INITIAL_POSITION.x}
                    cy={INITIAL_POSITION.y}
                    r="250"
                    fill={`url(#${glowId})`}
                    style={{
                        mixBlendMode: 'screen',
                        pointerEvents: 'none',
                    }}
                />
            </svg>
        </div>
    );
};

export default TriangularGridBackground;
